import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { driversApi, Driver } from '../services/driversApi';
import { lookupApi, LookupItem } from '../services/lookupApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const Drivers: React.FC = () => {
    const { t } = useLanguage();
    
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingDriver, setEditingDriver] = useState<Driver | null>(null);

    // Lookups state
    const [affectations, setAffectations] = useState<LookupItem[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);

    const formatDateForInput = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState<Partial<Driver>>({
        firstName: '',
        lastName: '',
        licenseNumber: '',
        licenseType: 'B',
        experienceYears: 0,
        status: 'active',
        phone: '',
        telegramChatId: '',
        function: '',
        affectationId: undefined,
        vehicleId: undefined,
        tempVehicleId: undefined,
        isTempReplacement: false,
        licenseExpiry: new Date(),
        hireDate: new Date(),
        assignedDate: undefined
    });

    const loadDrivers = async () => {
        setLoading(true);
        try {
            const response = await driversApi.getAll();
            if (response.success) {
                setDrivers(response.data);
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Failed to load drivers:', error);
            blobticMessage.error(t('common.messages.error') || 'Error', 'Failed to load drivers');
        } finally {
            setLoading(false);
        }
    };

    const loadLookups = async () => {
        try {
            const [affRes, vehRes] = await Promise.all([
                lookupApi.affectations.getAll(),
                vehiclesApi.getAll()
            ]);
            if (affRes.success) setAffectations(affRes.data);
            if (vehRes.success) setVehicles(vehRes.data);
        } catch (error) {
            console.error('Failed to load lookups for driver page:', error);
        }
    };

    useEffect(() => {
        loadDrivers();
        loadLookups();
    }, []);

    const handleOpenDialog = (driver?: Driver) => {
        if (driver) {
            setEditingDriver(driver);
            setFormData({
                ...driver,
                licenseExpiry: driver.licenseExpiry ? new Date(driver.licenseExpiry) : undefined,
                hireDate: driver.hireDate ? new Date(driver.hireDate) : undefined,
                assignedDate: driver.assignedDate ? new Date(driver.assignedDate) : undefined
            });
        } else {
            setEditingDriver(null);
            setFormData({
                firstName: '',
                lastName: '',
                licenseNumber: '',
                licenseType: 'B',
                experienceYears: 0,
                status: 'active',
                phone: '',
                telegramChatId: '',
                function: '',
                affectationId: affectations.length > 0 ? affectations[0].id : undefined,
                vehicleId: undefined,
                tempVehicleId: undefined,
                isTempReplacement: false,
                licenseExpiry: new Date(),
                hireDate: new Date(),
                assignedDate: undefined
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            let response;
            if (editingDriver) {
                response = await driversApi.update(editingDriver.id, formData);
            } else {
                response = await driversApi.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success') || 'Success', t('common.messages.saved') || 'Saved successfully');
                setIsDialogOpen(false);
                loadDrivers();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving driver:', error);
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Error occurred');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.actions.deleteConfirm') || 'Are you sure?')) return;
        try {
            const response = await driversApi.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success') || 'Success', t('common.messages.deleted') || 'Deleted successfully');
                loadDrivers();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Error occurred');
        }
    };

    const columns = [
        { id: 'firstName', label: t('parc.drivers.firstName') || 'Prénom', minWidth: 100 },
        { id: 'lastName', label: t('parc.drivers.lastName') || 'Nom', minWidth: 100 },
        { id: 'function', label: 'Fonction', minWidth: 120 },
        { id: 'departmentName', label: 'Affectation', minWidth: 120 },
        { id: 'vehicleRegistration', label: 'Véhicule', minWidth: 110 },
        { id: 'phone', label: 'Téléphone', minWidth: 100 },
        { id: 'telegramChatId', label: 'Chat ID Telegram', minWidth: 110 },
        { id: 'licenseNumber', label: t('parc.drivers.licenseNumber') || 'Permis N°', minWidth: 110 },
        { id: 'licenseType', label: t('parc.drivers.licenseType') || 'Catégorie', minWidth: 80 },
        {
            id: 'status',
            label: t('parc.drivers.status') || 'Statut',
            minWidth: 100,
            format: (value: string) => {
                if (value === 'active' || value === 'Actif') return 'Actif';
                if (value === 'inactive' || value === 'Inactif') return 'Inactif';
                if (value === 'suspended' || value === 'Suspendu') return 'Suspendu';
                if (value === 'on_leave' || value === 'En congé') return 'En congé';
                return value;
            }
        }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.drivers.newDriver') || 'Nouveau Conducteur'}
        </Button>
    );

    return (
        <GenericPage title={t('parc.drivers.title') || 'Conducteurs'} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={drivers}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingDriver ? (t('parc.drivers.editDriver') || 'Modifier Conducteur') : (t('parc.drivers.newDriver') || 'Nouveau Conducteur')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.drivers.firstName') || 'Prénom'}
                                value={formData.firstName || ''}
                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.drivers.lastName') || 'Nom'}
                                value={formData.lastName || ''}
                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Fonction"
                                value={formData.function || ''}
                                onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Affectation</InputLabel>
                                <Select
                                    value={formData.affectationId || ''}
                                    label="Affectation"
                                    onChange={(e) => setFormData({ ...formData, affectationId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    {affectations.map((a) => (
                                        <MenuItem key={a.id} value={a.id}>{a.lib}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Véhicule affecté</InputLabel>
                                <Select
                                    value={formData.vehicleId || ''}
                                    label="Véhicule affecté"
                                    onChange={(e) => setFormData({ ...formData, vehicleId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    <MenuItem value=""><em>Aucun</em></MenuItem>
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>{v.registrationNumber} ({v.brand} {v.model})</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date d'affectation véhicule"
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.assignedDate)}
                                onChange={(e) => setFormData({ ...formData, assignedDate: e.target.value ? new Date(e.target.value) : undefined })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Téléphone"
                                value={formData.phone || ''}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Chat ID Telegram"
                                value={formData.telegramChatId || ''}
                                onChange={(e) => setFormData({ ...formData, telegramChatId: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('parc.drivers.licenseNumber') || 'Permis N°'}
                                value={formData.licenseNumber || ''}
                                onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.drivers.licenseType') || 'Catégorie'}</InputLabel>
                                <Select
                                    value={formData.licenseType || 'B'}
                                    label={t('parc.drivers.licenseType') || 'Catégorie'}
                                    onChange={(e) => setFormData({ ...formData, licenseType: e.target.value })}
                                >
                                    <MenuItem value="A">A</MenuItem>
                                    <MenuItem value="B">B</MenuItem>
                                    <MenuItem value="C">C</MenuItem>
                                    <MenuItem value="D">D</MenuItem>
                                    <MenuItem value="E">E</MenuItem>
                                    <MenuItem value="F">F</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Expiration du permis"
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.licenseExpiry)}
                                onChange={(e) => setFormData({ ...formData, licenseExpiry: e.target.value ? new Date(e.target.value) : undefined })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date d'embauche"
                                InputLabelProps={{ shrink: true }}
                                value={formatDateForInput(formData.hireDate)}
                                onChange={(e) => setFormData({ ...formData, hireDate: e.target.value ? new Date(e.target.value) : undefined })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.drivers.experienceYears') || 'Années d\'expérience'}
                                value={formData.experienceYears || 0}
                                onChange={(e) => setFormData({ ...formData, experienceYears: Number(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.drivers.status') || 'Statut'}</InputLabel>
                                <Select
                                    value={formData.status || 'active'}
                                    label={t('parc.drivers.status') || 'Statut'}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <MenuItem value="active">Actif</MenuItem>
                                    <MenuItem value="inactive">Inactif</MenuItem>
                                    <MenuItem value="suspended">Suspendu</MenuItem>
                                    <MenuItem value="on_leave">En congé</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Remplacement Temporaire</InputLabel>
                                <Select
                                    value={formData.isTempReplacement ? 'true' : 'false'}
                                    label="Remplacement Temporaire"
                                    onChange={(e) => setFormData({ ...formData, isTempReplacement: e.target.value === 'true' })}
                                >
                                    <MenuItem value="false">Non</MenuItem>
                                    <MenuItem value="true">Oui</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Véhicule temporaire</InputLabel>
                                <Select
                                    value={formData.tempVehicleId || ''}
                                    label="Véhicule temporaire"
                                    onChange={(e) => setFormData({ ...formData, tempVehicleId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    <MenuItem value=""><em>Aucun</em></MenuItem>
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>{v.registrationNumber} ({v.brand} {v.model})</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default Drivers;
